import { z } from "zod";
import { AbEntitySchema, AbRunStatsSchema } from "./abEnrich.ts";
import { DbBool, IsoDateTime, LocalIdSchema, SpotifyIdSchema, jsonColumn } from "./common.ts";

export const MB_ENTITY_TYPES = [
  "artist",
  "recording",
  "release",
  "release-group",
  "work",
  "label",
  "place",
  "area",
  "url",
] as const;
export const MbEntityTypeSchema = z.enum(MB_ENTITY_TYPES);
export type MbEntityType = z.infer<typeof MbEntityTypeSchema>;

export const MB_LINK_ENTITY_TYPES = ["artist", "track", "album"] as const;
export const MbLinkEntityTypeSchema = z.enum(MB_LINK_ENTITY_TYPES);
export type MbLinkEntityType = z.infer<typeof MbLinkEntityTypeSchema>;

export const MB_FETCH_STATUSES = ["ok", "partial", "failed"] as const;
export const MbFetchStatusSchema = z.enum(MB_FETCH_STATUSES);
export type MbFetchStatus = z.infer<typeof MbFetchStatusSchema>;

export const MB_MATCH_METHODS = [
  "isrc",
  "from_recording",
  "exact_name",
  "fuzzy_name",
  "manual",
  "not_in_mb",
] as const;
export const MbMatchMethodSchema = z.enum(MB_MATCH_METHODS);
export type MbMatchMethod = z.infer<typeof MbMatchMethodSchema>;

export const MbidSchema = z.string().uuid();

export const MbBatchSizes = [5, 10, 25, 100, 500, 1000, 2000, 3000, 4000, "all"] as const;
export const MbBatchSizeSchema = z.union([
  z.literal(5),
  z.literal(10),
  z.literal(25),
  z.literal(100),
  z.literal(500),
  z.literal(1000),
  z.literal(2000),
  z.literal(3000),
  z.literal(4000),
  z.literal("all"),
]);
export type MbBatchSize = z.infer<typeof MbBatchSizeSchema>;

export const MbCandidateSchema = z.object({
  mbid: MbidSchema,
  name: z.string(),
  score: z.number().min(0).max(100),
  disambiguation: z.string().nullable(),
  type: z.string().nullable(),
  country: z.string().nullable(),
});
export type MbCandidate = z.infer<typeof MbCandidateSchema>;

export const MbEntitySchema = z.object({
  mbid: MbidSchema,
  entity_type: MbEntityTypeSchema,
  raw_json: jsonColumn(z.unknown()),
  /** Previous raw_json before last force-refetch (last-2 history). */
  previous_raw_json: jsonColumn(z.unknown()).nullable().optional().default(null),
  inc_used: z.string().nullable(),
  fetched_at: IsoDateTime,
  fetch_status: MbFetchStatusSchema,
});
export type MbEntity = z.infer<typeof MbEntitySchema>;

export const MbLinkSchema = z.object({
  entity_type: MbLinkEntityTypeSchema,
  entity_id: SpotifyIdSchema,
  mbid: MbidSchema.nullable(),
  match_method: MbMatchMethodSchema.nullable(),
  match_score: z.number().min(0).max(100).nullable(),
  locked: DbBool,
  linked_at: IsoDateTime.nullable(),
  candidates: jsonColumn(z.array(MbCandidateSchema).nullable()),
  note: z.string().nullable(),
});
export type MbLink = z.infer<typeof MbLinkSchema>;

export const MB_RUN_STATUSES = ["running", "paused", "stopped", "done", "error"] as const;
export const MbRunStatusSchema = z.enum(MB_RUN_STATUSES);

export const MbTypeCountSchema = z.object({
  entity_type: z.string(),
  new_count: z.number().int().nonnegative(),
  partial_count: z.number().int().nonnegative(),
});

export const MbFieldCoverageSchema = z.object({
  label: z.string(),
  have: z.number().int().nonnegative(),
  of: z.number().int().nonnegative(),
});

export const MbRunSummarySchema = z.object({
  tracks_processed: z.number().int().nonnegative(),
  requests_made: z.number().int().nonnegative(),
  elapsed_ms: z.number().int().nonnegative(),
  by_type: z.array(MbTypeCountSchema),
  field_coverage: z.array(MbFieldCoverageSchema),
  new_mbids: z.array(
    z.object({
      mbid: MbidSchema,
      entity_type: MbEntityTypeSchema,
      name: z.string().nullable(),
      fetch_status: MbFetchStatusSchema,
    }),
  ),
  /** AcousticBrainz attempts in this run (after recording MBID). */
  acousticbrainz: AbRunStatsSchema.optional(),
});
export type MbRunSummary = z.infer<typeof MbRunSummarySchema>;

export const MbTrackPulseSchema = z.object({
  name: z.string().nullable(),
  requests: z.number().int().nonnegative(),
  by_type: z.array(
    z.object({
      entity_type: z.string(),
      count: z.number().int().nonnegative(),
    }),
  ),
});
export type MbTrackPulse = z.infer<typeof MbTrackPulseSchema>;

export const MbEnrichRunSchema = z.object({
  status: MbRunStatusSchema,
  mode: z.enum(["batch", "entity"]),
  batch_size: MbBatchSizeSchema.nullable(),
  track_ids: z.array(z.string()).nullable(),
  entity_type: MbLinkEntityTypeSchema.nullable(),
  entity_id: z.string().nullable(),
  /** Spotify playlist scope for batch runs; null = liked-first library default. */
  playlist_id: SpotifyIdSchema.nullable().optional(),
  /** Local playlist scope for batch runs; null = not scoped to a local playlist. */
  local_playlist_id: LocalIdSchema.nullable().optional(),
  /** When true, re-fetch even if mb_entities already has ok rows / tracks are linked. */
  force: z.boolean().optional(),
  requests_made: z.number().int().nonnegative(),
  tracks_done: z.number().int().nonnegative(),
  tracks_total: z.number().int().nonnegative(),
  queue_depth: z.number().int().nonnegative(),
  current_url: z.string().nullable(),
  /** Human label for the track currently in the cascade (playlist / batch progress). */
  current_track: z.string().nullable().optional(),
  /**
   * Cascade phase for the current track — e.g. "ISRC lookup", "Browsing releases",
   * "Fetching artist", "AcousticBrainz".
   */
  current_step: z.string().nullable().optional(),
  /** Counts from the track that just finished (what was found, not the payload). */
  last_track: MbTrackPulseSchema.nullable().optional(),
  started_at: IsoDateTime,
  updated_at: IsoDateTime,
  finished_at: IsoDateTime.nullable(),
  cursor_track_id: z.string().nullable(),
  error: z.string().nullable(),
  summary: MbRunSummarySchema.nullable(),
  /** Opt-in name search when entity enrich has no ISRC seed. */
  allow_name_search: z.boolean().optional(),
});
export type MbEnrichRun = z.infer<typeof MbEnrichRunSchema>;

export const MbSettingsSchema = z.object({
  contact: z.string(),
  contact_source: z.enum(["settings", "env", "missing"]),
});

export const MbSettingsPatchSchema = z.object({
  contact: z.string().trim().min(3).max(200),
});

export const MbStartBatchSchema = z.object({
  size: MbBatchSizeSchema.default(10),
  /** When set, feed the queue from this Spotify playlist instead of liked-first. */
  playlist_id: SpotifyIdSchema.optional(),
  /** When set, feed the queue from this local playlist instead of liked-first. */
  local_playlist_id: LocalIdSchema.optional(),
  /** Re-fetch even when tracks / MBIDs are already stored. */
  force: z.boolean().optional(),
}).refine((body) => !(body.playlist_id && body.local_playlist_id), {
  message: "Provide either playlist_id or local_playlist_id, not both",
  path: ["local_playlist_id"],
});

export const MbStartEntitySchema = z.object({
  entity_type: MbLinkEntityTypeSchema,
  entity_id: SpotifyIdSchema,
  force: z.boolean().optional(),
  /** Explicit opt-in when no ISRC seed exists — runs artist name search. */
  allow_name_search: z.boolean().optional(),
});

export const MbManualLinkSchema = z.object({
  mbid: MbidSchema,
});

export const MbEstimateSchema = z.object({
  tracks: z.number().int().nonnegative(),
  requests_min: z.number().int().nonnegative(),
  requests_max: z.number().int().nonnegative(),
  time_sec_min: z.number().int().nonnegative(),
  time_sec_max: z.number().int().nonnegative(),
});
export type MbEstimate = z.infer<typeof MbEstimateSchema>;

export const MbStatusSchema = z.object({
  settings: MbSettingsSchema,
  unenriched_tracks: z.number().int().nonnegative(),
  enriched_tracks: z.number().int().nonnegative(),
  entity_counts: z.record(z.string(), z.number().int().nonnegative()),
  link_counts: z.object({
    locked: z.number().int().nonnegative(),
    ambiguous: z.number().int().nonnegative(),
    not_in_mb: z.number().int().nonnegative(),
  }),
  estimates: z.record(z.string(), MbEstimateSchema),
  run: MbEnrichRunSchema.nullable(),
  /**
   * Batch order for the active scope: liked-first by added_at desc, or playlist
   * tracks by playlist added_at desc.
   */
  batch_order: z.enum([
    "liked_added_at_desc",
    "playlist_added_at_desc",
    "local_playlist_position",
  ]),
  /** Echo of the optional playlist scope used for unenriched / estimates. */
  playlist_id: SpotifyIdSchema.nullable(),
  playlist_name: z.string().nullable(),
  /** Echo of the optional local playlist scope used for unenriched / estimates. */
  local_playlist_id: LocalIdSchema.nullable().default(null),
  local_playlist_name: z.string().nullable().default(null),
});
export type MbStatus = z.infer<typeof MbStatusSchema>;

/** One field row in the coverage dashboard (filled among enriched). */
export const MbCoverageFieldSchema = z.object({
  label: z.string(),
  filled: z.number().int().nonnegative(),
  of: z.number().int().nonnegative(),
  pct: z.number().nonnegative(),
});
export type MbCoverageField = z.infer<typeof MbCoverageFieldSchema>;

export const MbCoverageRelTypeSchema = z.object({
  type: z.string(),
  /** Artists that have ≥1 relation of this type. */
  filled: z.number().int().nonnegative(),
});

export const MbCoverageTableSchema = z.object({
  /** Display entity: MusicBrainz side (recording/release) or artist. */
  entity_kind: z.enum(["artist", "recording", "release"]),
  enriched: z.number().int().nonnegative(),
  total: z.number().int().nonnegative(),
  fields: z.array(MbCoverageFieldSchema),
  /** Cheap breakdown of artist-relation types (artists table only). */
  rel_types: z.array(MbCoverageRelTypeSchema).optional(),
});
export type MbCoverageTable = z.infer<typeof MbCoverageTableSchema>;

export const MbCoverageProgressSchema = z.object({
  entity_kind: z.enum(["artist", "track", "album"]),
  with_mbid: z.number().int().nonnegative(),
  total: z.number().int().nonnegative(),
  pct: z.number().nonnegative(),
});

export const MbCoverageSchema = z.object({
  playlist_id: SpotifyIdSchema.nullable(),
  playlist_name: z.string().nullable(),
  progress: z.array(MbCoverageProgressSchema),
  tables: z.array(MbCoverageTableSchema),
});
export type MbCoverage = z.infer<typeof MbCoverageSchema>;

/** Compact enrich state for the detail sheet Enrich tab. */
export const MbEnrichDetailSchema = z.object({
  link: MbLinkSchema.nullable(),
  entity: MbEntitySchema.nullable(),
  reason: z.string().nullable(),
  /** Present for tracks when a recording MBID exists (ok / not_found / …). */
  acousticbrainz: AbEntitySchema.nullable().optional(),
});
export type MbEnrichDetail = z.infer<typeof MbEnrichDetailSchema>;

export const MbWipeResultSchema = z.object({
  entities_deleted: z.number().int().nonnegative(),
  links_deleted: z.number().int().nonnegative(),
  ab_deleted: z.number().int().nonnegative(),
  manual_kept: z.number().int().nonnegative(),
});
export type MbWipeResult = z.infer<typeof MbWipeResultSchema>;

export const MbWipeBodySchema = z.object({
  confirm: z.literal(true),
});

/** Single-track cascade dump for eyeballing stored JSON. */
export const MbCascadeDumpSchema = z.object({
  track_id: SpotifyIdSchema,
  link: MbLinkSchema.nullable(),
  entities: z.array(
    z.object({
      mbid: MbidSchema,
      entity_type: MbEntityTypeSchema,
      name: z.string().nullable(),
      fetch_status: MbFetchStatusSchema,
      inc_used: z.string().nullable(),
      fetched_at: IsoDateTime,
      raw_json: z.unknown(),
    }),
  ),
});
export type MbCascadeDump = z.infer<typeof MbCascadeDumpSchema>;
